// File: packages/components/src/agent_profile/quotes/QuotesCV.tsx (Refactored)

import React from 'react';
import type { Quote, CalculatorSpec, Module } from '@dopomogai/types';
import { Card, Badge } from '@dopomogai/ui';
import { CheckCircle } from 'lucide-react';

// A helper function to find selected module details from the spec
const getSelectedModules = (spec: CalculatorSpec, ids: string[]): Module[] => {
  const selectedIds = new Set(ids);
  return spec.groups.flatMap(g => g.features).filter(f => selectedIds.has(f.id));
};

// --- NEW PROP DEFINITION ---
type Props = {
  // Core agent identity and configuration, derived from either an AgentVersion or a Quote snapshot.
  agentData: {
    agentName: string;
    heroTitle: string;
    heroSubtitle?: string;
    avatarUrl?: string;
    calculator_config?: CalculatorSpec;
    selected_module_ids: string[];
  };

  // Optional financial data from a Quote. If not provided, pricing sections are hidden.
  quoteData?: {
    totals: Quote['totals'];
    line_items: Quote['line_items'];
    valid_until: Quote['valid_until'];
  };

  // Theming
  accentHex?: string;
};

// --- REFACTORED COMPONENT ---
export const QuotesCV = React.forwardRef<HTMLDivElement, Props>(
  ({ agentData, quoteData, accentHex = '#10B981' }, ref) => {
    
    // Derive the list of capabilities from the selected modules
    const selectedModules = agentData.calculator_config
      ? getSelectedModules(agentData.calculator_config, agentData.selected_module_ids)
      : [];

    return (
      <div ref={ref} className="bg-white p-8 md:p-12 shadow-2xl rounded-2xl max-w-4xl mx-auto border-2 border-black print:shadow-none print:border-none">
        <style>{`
          .cv-accent { color: ${accentHex}; }
          .cv-accent-bg { background-color: ${accentHex}; }
        `}</style>
        
        {/* --- Header --- */}
        <header className="flex flex-col md:flex-row items-start gap-8 border-b-2 border-black pb-8">
          {agentData.avatarUrl && (
            <img src={agentData.avatarUrl} alt={`${agentData.agentName} avatar`} className="w-32 h-32 rounded-full object-cover border-4 border-black" />
          )}
          <div className="flex-1">
            <h1 className="text-4xl md:text-5xl font-black text-slate-900">{agentData.heroTitle}</h1>
            <p className="text-lg text-slate-600 mt-2">{agentData.heroSubtitle || `A custom AI agent for your business.`}</p>
          </div>
        </header>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-8">
          {/* --- Left Column (Details & Pricing) --- */}
          <div className="md:col-span-2 space-y-8">
            <section>
              <h2 className="text-2xl font-bold cv-accent mb-4">Core Capabilities</h2>
              <ul className="space-y-2">
                {selectedModules.map(mod => (
                  <li key={mod.id} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 mt-1 text-emerald-500 flex-shrink-0" />
                    <div>
                      <span className="font-semibold text-slate-800">{mod.label}</span>
                      <p className="text-sm text-slate-600">{mod.desc}</p>
                    </div>
                  </li>
                ))}
                 {selectedModules.length === 0 && <p className="text-slate-500">Core capabilities defined by base configuration.</p>}
              </ul>
            </section>

            {/* --- CONDITIONAL QUOTE SECTION --- */}
            {quoteData && (
              <section className="border-t-2 border-black pt-8">
                <h2 className="text-2xl font-bold cv-accent mb-4">Plan & Investment</h2>
                <div className="border-2 border-black rounded-xl p-4 space-y-3">
                  {quoteData.line_items.map(item => (
                    <div key={item.id} className="flex justify-between items-center text-slate-700">
                      <span>{item.label}</span>
                      <span className="font-mono font-semibold">${item.price_usd.toLocaleString()}</span>
                    </div>
                  ))}
                  <div className="flex justify-between items-center text-slate-900 font-bold border-t border-slate-200 pt-3 mt-3">
                    <span>One-Time Setup Total</span>
                    <span className="font-mono text-xl">${quoteData.totals.setup_usd.toLocaleString()}</span>
                  </div>
                </div>
              </section>
            )}
          </div>

          {/* --- Right Column (Meta Info) --- */}
          <aside className="space-y-6">
            <Card noHoverEffect>
              <h3 className="font-bold text-lg mb-2">Details</h3>
              <p><span className="font-semibold">Agent Name:</span> {agentData.agentName}</p>
              <p><span className="font-semibold">Plan Tier:</span> <Badge text={quoteData?.totals.tier.toUpperCase() || 'Custom'} accentColor="blue" /></p>
            </Card>

            {/* --- CONDITIONAL QUOTE META --- */}
            {quoteData && (
              <Card noHoverEffect>
                 <h3 className="font-bold text-lg mb-2">Quote Details</h3>
                <p><span className="font-semibold">ETA:</span> {quoteData.totals.eta_days[0]}-{quoteData.totals.eta_days[1]} days</p>
                <p><span className="font-semibold">Valid Until:</span> {new Date(quoteData.valid_until).toLocaleDateString()}</p>
              </Card>
            )}
          </aside>
        </div>

        <footer className="text-center text-xs text-slate-400 mt-12 pt-6 border-t border-slate-200">
          Generated by Dopomogai Platform | {new Date().toLocaleDateString()}
        </footer>
      </div>
    );
  }
);